import React, { useState, useEffect, useRef } from "react";
import { CardV2, CardContent } from "../../components/CardV2/CardV2";
import { InputV2 } from "../../components/InputV2/InputV2";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/TableV2/Table";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageWrapper from "../PageWrapper/PageWrapper";
import { CreditTopUpModal } from "../../components/CreditTopUpModal/CreditTopUpModal";
import { Partner } from "../../types/partner";
import { RevealPartnerDialog } from "../../components/RevealPartnerDialog/RevealPartnerDialog";
import { motion } from "framer-motion";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import FilterPill from "../../components/FilterPill/FilterPill";
import { DiscoveryAPI } from '../../api/DiscoveryAPI';
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import PartnerTableRow from "../../components/PartnerTableRow";
import RevealAnimation from "../../components/RevealAnimation";
import ReactSlider from 'react-slider';
import './FindPartnersPageSlider.css';
import BetaWarning from "../../components/BetaWarning/BetaWarning";
import { toastService } from "../../services/ToastService";

const PARTNER_CATEGORIES = ["Brand", "Retailer", "Second hand", "Outlet"];

function SocialFollowersRangeFilter({ value, onChange }: { value: [number, number]; onChange: (v: [number, number]) => void }) {
  
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col w-full max-w-md">
      <label className="text-xs font-medium mb-1 text-darker-gold">{t("partners.find.social_media_followers", "Social Media Followers")}</label>
      <div className="flex items-center gap-4 w-full">
        <span className="text-xs text-primary font-semibold w-12 text-right">{value[0]}</span>
        <ReactSlider
          className="slider-range flex-1"
          thumbClassName="slider-thumb"
          trackClassName="slider-track"
          min={0}
          max={200000}
          step={1000}
          value={value}
          onChange={onChange}
          minDistance={1000}
          pearling
          renderThumb={(props, state) => {
            const { style, ...rest } = props as React.HTMLAttributes<HTMLDivElement>;
            return (
              <div
                {...rest}
                className="slider-thumb"
                style={{ ...style, top: '50%', transform: 'translateY(-50%)' }}
              />
            );
          }}
          renderTrack={(props, state) => {
            const { style, ...rest } = props as React.HTMLAttributes<HTMLDivElement>;
            return (
              <div
                {...rest}
                className={
                  state.index === 1
                    ? 'slider-track slider-track-active'
                    : 'slider-track'
                }
                style={style}
              />
            );
          }}
        />
        <span className="text-xs text-primary font-semibold w-12">{value[1]}</span>
      </div>
    </div>
  );
}

// TypeFilterPills component
function TypeFilterPills({ categories, activeCategory, onCategoryChange }: { categories: string[]; activeCategory: string | null; onCategoryChange: (cat: string) => void }) {
  const { t } = useTranslation();
  const categoryLabels: Record<string, string> = {
    "Brand": t("partners.find.brand", "Brand"),
    "Retailer": t("partners.find.retailer", "Retailer"),
    "Second hand": t("partners.find.second_hand", "Second Hand"),
    "Outlet": t("partners.find.outlet", "Outlet"),
  };
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((category) => (
        <FilterPill
          key={category}
          text={categoryLabels[category] || category}
          isActive={activeCategory === category}
          fn={() => onCategoryChange(category)}
        />
      ))}
    </div>
  );
}

function getHostname(url?: string | null): string {
  if (!url) return '';
  // If the value is obfuscated (all dots or all bullets), don't try to parse it
  if (/^[•.]+$/.test(url)) return url;
  try {
    // Add protocol if missing
    const withProtocol = url.match(/^https?:\/\//) ? url : `https://${url}`;
    return new URL(withProtocol).hostname;
  } catch {
    return url; // fallback to showing the raw string
  }
}

// Calculate quality rating on the frontend
function calculateQualityRating(partner: Partner): number {
  const followers = partner.followers || 0;
  const partnersCount = partner.partnersWith ? partner.partnersWith.length : 0;
  if (followers > 10000 && partnersCount > 5) return 5;
  if (followers > 5000 && partnersCount > 2) return 4;
  if (followers > 1000 && partnersCount >= 1) return 3;
  if (followers > 1000 || partnersCount >= 1) return 2;
  return 1;
}

export default function FindPartnersPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedPartner, setExpandedPartner] = useState<number | null>(null);
  const [searchCredits, setSearchCredits] = useState(0);
  const [, setMaxCredits] = useState(0);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [partnerToReveal, setPartnerToReveal] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, ] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedCountry, ] = useState<string | null>(null);
  const [selectedType, ] = useState<string | null>(null);
  const [socialFollowersRange, setSocialFollowersRange] = useState<[number, number]>([0, 200000]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLoading(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      DiscoveryAPI.getPartners({
        query: searchQuery,
        country: selectedCountry || undefined,
        type: selectedType || activeCategory || undefined,
        minInstagramFollowers: socialFollowersRange[0] !== 0 ? socialFollowersRange[0] : undefined,
        maxInstagramFollowers: socialFollowersRange[1] !== 200000 ? socialFollowersRange[1] : undefined,
        page,
        size: pageSize
      })
        .then((result) => {
          setFilteredPartners(result.content);
          setTotalPages(result.totalPages);
        })
        .finally(() => {
          setLoading(false);
          setInitialLoadDone(true);
        });
    }, 1000);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchQuery, selectedCountry, selectedType, activeCategory, socialFollowersRange, page, pageSize]);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceTimeout.current) clearTimeout(searchDebounceTimeout.current);
    searchDebounceTimeout.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 1000);
    return () => {
      if (searchDebounceTimeout.current) clearTimeout(searchDebounceTimeout.current);
    };
  }, [searchInput]);

  useEffect(() => {
    DiscoveryAPI.getCredits().then(credits => {
      setSearchCredits(credits);
      setMaxCredits(credits);
    });
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const topupSuccess = urlParams.get('topup_success');
    const topupCancelled = urlParams.get('topup_cancelled');
    const creditsAmount = urlParams.get('credits');

    if (topupSuccess === 'true' && creditsAmount) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      DiscoveryAPI.getCredits().then(credits => {
        setSearchCredits(credits);
        setMaxCredits(credits);
      });
      
      toastService.showToast(t("credits.top_up.success", "Successfully purchased {{credits}} credits!", { credits: creditsAmount }), "success");
    } else if (topupCancelled === 'true') {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      toastService.showToast(t("credits.top_up.cancelled", "Credit purchase was cancelled."), "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryFilter = (category: string) => {
    const newCategory = activeCategory === category ? null : category;
    setActiveCategory(newCategory);
    setPage(0);
  };

  const toggleExpandPartner = (partnerId: number) => {
    setExpandedPartner(expandedPartner === partnerId ? null : partnerId);
  };

  const handleRevealPartner = (partner: Partner) => {
    if (searchCredits < 1) {
      setIsTopUpModalOpen(true);
      return;
    }
    setPartnerToReveal(partner);
  };

  const confirmReveal = () => {
    if (partnerToReveal && searchCredits > 0) {
      DiscoveryAPI.revealPartner(partnerToReveal.id).then((revealedPartner) => {
        setFilteredPartners(prev =>
          prev.map(p => p.id === revealedPartner.id ? revealedPartner : p)
        );
        DiscoveryAPI.getCredits().then(credits => {
          setSearchCredits(credits);
          setMaxCredits(credits);
        });
        setPartnerToReveal(null);
      });
    }
  };

  const isPartnerRevealed = (partner: Partner) => partner.revealed;

  const formatFollowers = (count: number | null) => {
    if (!count) return "N/A";
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };
  
  const getQualityRatingColor = (rating: number | undefined) => {
    switch(rating) {
      case 5: return "bg-green-500";
      case 4: return "bg-green-400";
      case 3: return "bg-yellow-400";
      case 2: return "bg-orange-400";
      case 1: return "bg-red-500";
      default: return "bg-gray-400";
    }
  };
  
  const getQualityRatingText = (rating: number | undefined) => {
    switch(rating) {
      case 5: return "Excellent";
      case 4: return "Very Good";
      case 3: return "Good";
      case 2: return "Fair";
      case 1: return "Missing data";
      default: return "Unknown";
    }
  };

  const handleFollowersRangeChange = (range: [number, number]) => {
    setSocialFollowersRange(range);
    setPage(0);
  };

  return (
    <PageWrapper>
      <div className="space-y-6">
        <BetaWarning />
        <CardV2>
          <CardContent className="p-4">
            {/* Search bar row */}
            <div className="mb-4 flex items-center gap-2">
              <InputV2
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={t("partners.find.search_placeholder", "Search by city or country...").toString()}
                className="w-full"
              />
            </div>
            {/* Filters row */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 w-full gap-2">
              <TypeFilterPills categories={PARTNER_CATEGORIES} activeCategory={activeCategory} onCategoryChange={handleCategoryFilter} />
            </div>
            {/* Social Media Followers Range Filter */}
            <SocialFollowersRangeFilter value={socialFollowersRange} onChange={handleFollowersRangeChange} />
            {/* Top Up action row (below filters, left-aligned) */}
            <div className="mb-4">
              <div className="flex items-center space-x-4">
                <PrimaryButton
                  text={
                    <div className="flex items-center space-x-2">
                      <Plus className="h-4 w-4" />
                      <span>{t("credits.top_up.button", "Top Up")}</span>
                    </div>
                  }
                  fn={() => setIsTopUpModalOpen(true)}
                  eventName="top_up_credits_clicked"
                  actionName="Top Up Credits"
                />
                <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
                  {t("partners.find.amount_credits", "{{amount}} credits", { amount: searchCredits })}
                </div>
              </div>
            </div>
          </CardContent>
        </CardV2>
        <CardV2 className="pt-6">
          <CardContent className="min-h-[400px]">
            {/* Loading animation overlay */}
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b border-gray-200 bg-gray-50">
                  <TableHead className="px-6 py-4 text-left font-semibold text-gray-900 uppercase tracking-wide text-xs">
                    {t("partners.find.name", "Partner Name")}
                  </TableHead>
                  <TableHead className="px-6 py-4 text-left font-semibold text-gray-900 uppercase tracking-wide text-xs">
                    {t("partners.find.social", "Social Media")}
                  </TableHead>
                  <TableHead className="px-6 py-4 text-left font-semibold text-gray-900 uppercase tracking-wide text-xs">
                    {t("partners.find.contact", "Contact")}
                  </TableHead>
                  <TableHead className="px-6 py-4 text-left font-semibold text-gray-900 uppercase tracking-wide text-xs">
                    {t("partners.find.location", "Location")}
                  </TableHead>
                  <TableHead className="px-6 py-4 text-left font-semibold text-gray-900 uppercase tracking-wide text-xs">
                    {t("partners.find.partners", "Partners")}
                  </TableHead>
                  <TableHead className="px-6 py-4 text-right font-semibold text-gray-900 uppercase tracking-wide text-xs min-w-[120px]">
                    {t("partners.find.actions", "Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse w-full border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 w-full" colSpan={6}>
                        <div className="flex w-full gap-4">
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[180px]" />
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[140px]" />
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[160px]" />
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[120px]" />
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[100px]" />
                          <div className="h-4 bg-gray-200 rounded w-full max-w-[80px] ml-auto" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : filteredPartners.length > 0 ? (
                  filteredPartners.map((partner) => (
                    <PartnerTableRow
                      key={partner.id}
                      partner={partner}
                      expandedPartner={expandedPartner}
                      onExpand={toggleExpandPartner}
                      onReveal={handleRevealPartner}
                      isRevealed={isPartnerRevealed(partner)}
                      formatFollowers={formatFollowers}
                      getQualityRatingColor={getQualityRatingColor}
                      getQualityRatingText={getQualityRatingText}
                      calculateQualityRating={calculateQualityRating}
                      getHostname={getHostname}
                      renderExpandedRow={
                        expandedPartner === partner.id && (
                          <motion.tr
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-b border-gray-100"
                          >
                            <TableCell colSpan={7} className="bg-gray-50/50 px-6 py-4">
                              <RevealAnimation isRevealed={isPartnerRevealed(partner)}>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-700">{t("partners.find.partners", "Partners")}:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {partner.partnersWith && partner.partnersWith.length > 0 ? (
                                        partner.partnersWith.map((p, idx) => (
                                          <span key={idx} className="px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-700">{p}</span>
                                        ))
                                      ) : (
                                        <span className="text-xs text-gray-500">{t("partners.find.empty_partners", "No partners listed.")}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">{t("partners.find.category", "Category")}:</span>
                                    <span className="ml-2 text-gray-600">{partner.type}</span>
                                  </div>
                                  <div className="col-span-2 mt-2">
                                    <span className="font-medium text-gray-700">{t("partners.find.about_the_brand", "About the brand")}:</span>
                                    <p className="mt-1 text-gray-600">{partner.genericBrandInfo || "No information available."}</p>
                                  </div>
                                </div>
                              </RevealAnimation>
                            </TableCell>
                          </motion.tr>
                        )
                      }
                    />
                  ))
                ) : initialLoadDone ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      {t("partners.find.no_results", "No partners found").toString()}
                    </td>
                  </tr>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </CardV2>

        <CreditTopUpModal
          isOpen={isTopUpModalOpen}
          onClose={() => setIsTopUpModalOpen(false)}
        />

        <RevealPartnerDialog
          isOpen={!!partnerToReveal}
          onClose={() => setPartnerToReveal(null)}
          onConfirm={confirmReveal}
          partnerName={partnerToReveal?.businessName || ''}
          currentCredits={searchCredits}
        />

        <div className="flex justify-center mt-4 items-center gap-2">
          <SecondaryButton
            text={t("partners.find.prev", "Prev").toString()}
            fn={() => setPage(p => Math.max(0, p - 1))}
            isDisabled={page === 0}
            actionName="Pagination Prev"
          />
          <span className="px-3 py-1 mx-1">{t("partners.find.page", "Page {{page}} of {{totalPages}}", { page: page + 1, totalPages: totalPages })}</span>
          <SecondaryButton
            text={t("partners.find.next", "Next").toString()}
            fn={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            isDisabled={page >= totalPages - 1}
            actionName="Pagination Next"
          />
        </div>
      </div>
    </PageWrapper>
  );
} 